import { Controller } from "@hotwired/stimulus"

import { trackStripeCheckoutOpened } from "@/lib/posthog"

// Connects to data-controller="gratitude"
// Handles the gratitude modal flow: amount selection, custom input, form submission
export default class extends Controller {
  static targets = [
    "form",
    "amountInput",
    "customInput",
    "submitButton",
    "amountButton",
    "dialogTrigger",
  ]
  static values = {
    selectedAmount: { type: Number, default: 2500 }, // cents - $25 default
  }
  static classes = ["amountSelected", "amountUnselected"]

  declare readonly hasFormTarget: boolean
  declare readonly formTarget: HTMLFormElement
  declare readonly hasAmountInputTarget: boolean
  declare readonly amountInputTarget: HTMLInputElement
  declare readonly hasCustomInputTarget: boolean
  declare readonly customInputTarget: HTMLInputElement
  declare readonly hasSubmitButtonTarget: boolean
  declare readonly submitButtonTarget: HTMLInputElement
  declare readonly amountButtonTargets: HTMLButtonElement[]
  declare readonly hasDialogTriggerTarget: boolean
  declare readonly dialogTriggerTarget: HTMLButtonElement

  declare selectedAmountValue: number

  declare hasAmountSelectedClass: boolean
  declare hasAmountUnselectedClass: boolean
  declare amountSelectedClasses: string[]
  declare amountUnselectedClasses: string[]

  private isCustomMode = false
  private openModalTimeoutId: ReturnType<typeof setTimeout> | null = null
  private bannerTimeoutId: ReturnType<typeof setTimeout> | null = null

  connect() {
    // Initialize with default amount ($25)
    this.updateAmountInput(this.selectedAmountValue)
    this.updateButtonStates()

    // Check for ?gratitude= query parameter
    const urlParams = new URLSearchParams(window.location.search)
    const gratitudeParam = urlParams.get("gratitude")

    if (gratitudeParam === "open") {
      // Clean up URL to remove the gratitude parameter
      this.cleanupUrl()

      // Open modal after brief delay to ensure DOM is ready
      this.openModalTimeoutId = setTimeout(() => {
        this.openModalTimeoutId = null
        this.openGratitudeModal()
      }, 100)
    } else if (gratitudeParam === "success") {
      this.cleanupUrl()
      this.showBanner(
        "Thank you! Your gratitude has been sent.",
        "success",
        6000,
      )
    } else if (gratitudeParam === "cancel") {
      this.cleanupUrl()
      this.showBanner(
        "Payment was cancelled. You can try again anytime.",
        "cancel",
        4000,
      )
    }
  }

  disconnect() {
    // Clear any pending timeouts to prevent execution on stale controller
    if (this.openModalTimeoutId !== null) {
      clearTimeout(this.openModalTimeoutId)
      this.openModalTimeoutId = null
    }
    if (this.bannerTimeoutId !== null) {
      clearTimeout(this.bannerTimeoutId)
      this.bannerTimeoutId = null
    }
  }

  // Open the gratitude modal. Anonymous visitors go straight in: checkout
  // itself collects the email and creates the account (#1094, #1601), so
  // there is nothing to join first.
  openGratitudeModal() {
    if (this.hasDialogTriggerTarget) {
      this.dialogTriggerTarget.click()
    }
  }

  // Handle preset amount button click using Stimulus action params
  // HTML: data-action="click->gratitude#selectAmount" data-gratitude-amount-param="1000"
  selectAmount(event: Event & { params: { amount: number } }) {
    const amount = event.params.amount
    this.isCustomMode = false
    this.selectedAmountValue = amount
    this.updateAmountInput(amount)
    this.updateButtonStates()

    // Clear custom input when selecting preset
    if (this.hasCustomInputTarget) {
      this.customInputTarget.value = ""
    }
  }

  // Handle custom input focus - deselects preset buttons
  handleCustomFocus() {
    this.isCustomMode = true
    this.updateButtonStates()
  }

  // Handle custom amount input change
  handleCustomInput(event: Event) {
    const input = event.target as HTMLInputElement
    const dollars = parseFloat(input.value) || 0
    const cents = Math.round(dollars * 100)

    this.isCustomMode = true
    this.selectedAmountValue = cents
    this.updateAmountInput(cents)
    this.updateButtonStates()
  }

  submit(event: Event) {
    event.preventDefault()

    trackStripeCheckoutOpened("gratitude")

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.value = "Processing..."
    }

    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  // Private methods

  private cleanupUrl() {
    const url = new URL(window.location.href)
    url.searchParams.delete("gratitude")
    window.history.replaceState({}, "", url.pathname + url.search)
  }

  private updateAmountInput(cents: number) {
    if (this.hasAmountInputTarget) {
      this.amountInputTarget.value = cents.toString()
    }
  }

  private updateButtonStates() {
    // Only proceed if classes are configured
    if (!this.hasAmountSelectedClass || !this.hasAmountUnselectedClass) {
      return
    }

    const selectedClasses = this.amountSelectedClasses
    const unselectedClasses = this.amountUnselectedClasses

    this.amountButtonTargets.forEach((button) => {
      const amount = parseInt(button.dataset.amount ?? "0", 10)
      const isSelected =
        !this.isCustomMode && amount === this.selectedAmountValue

      if (isSelected) {
        button.classList.add(...selectedClasses)
        button.classList.remove(...unselectedClasses)
      } else {
        button.classList.remove(...selectedClasses)
        button.classList.add(...unselectedClasses)
      }
    })
  }

  private showBanner(
    message: string,
    type: "success" | "cancel",
    duration: number,
  ) {
    const banner = document.createElement("div")
    banner.setAttribute("data-testid", `gratitude-${type}-banner`)
    banner.setAttribute("role", "status")

    const baseClasses =
      "mb-4 rounded-lg px-4 py-3 text-sm font-medium transition-opacity duration-300"
    if (type === "success") {
      banner.className = `${baseClasses} bg-green-50 text-green-800 border border-green-200`
    } else {
      banner.className = `${baseClasses} bg-gray-50 text-gray-600 border border-gray-200`
    }

    banner.textContent = message
    this.element.insertBefore(banner, this.element.firstChild)

    this.bannerTimeoutId = setTimeout(() => {
      this.bannerTimeoutId = null
      banner.style.opacity = "0"
      setTimeout(() => banner.remove(), 300)
    }, duration)
  }
}
